import React, { useState, useEffect } from 'react';
import { operatingService, searchCasesAsync } from '../../services/api';
import { X, DollarSign, AlertCircle, Check } from 'lucide-react';
import useModal from '../../hooks/useModal';
import AsyncSearchableSelect from '../common/AsyncSearchableSelect';

const NewOperatingTransactionModal = ({ isOpen, onClose, accountId, onTransactionCreated }) => {
  const [formData, setFormData] = useState({
    is_credit: true,
    amount: '',
    transaction_date: new Date().toISOString().split('T')[0],
    description: '',
    client_name: '',
    case_id: '',
    payment_method: '',
    payment_notes: '',
    is_refund: false
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const { handleBackdropMouseDown, handleBackdropMouseUp } = useModal(isOpen, onClose);

  useEffect(() => {
    if (isOpen) {
      setFormData({
        is_credit: true,
        amount: '',
        transaction_date: new Date().toISOString().split('T')[0],
        description: '',
        client_name: '',
        case_id: '',
        payment_method: '',
        payment_notes: '',
        is_refund: false
      });
      setError('');
    }
  }, [isOpen]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      if (!formData.amount || !formData.transaction_date) {
        setError('Amount and date are required');
        return;
      }

      let amount = parseFloat(formData.amount);
      if (isNaN(amount) || amount === 0) {
        setError('Please enter a valid amount');
        return;
      }

      // Make debits negative
      if (!formData.is_credit && amount > 0) {
        amount = -amount;
      }

      const transactionData = {
        account_id: accountId,
        amount: amount,
        transaction_date: formData.transaction_date,
        description: formData.description || null,
        client_name: formData.client_name || null,
        case_id: formData.case_id || null,
        payment_method: formData.payment_method || null,
        payment_notes: formData.payment_notes || null,
        is_refund: formData.is_refund,
        is_credit: formData.is_credit
      };

      const newTransaction = await operatingService.createTransaction(transactionData);

      if (onTransactionCreated) {
        onTransactionCreated(newTransaction);
      }

      onClose();
    } catch (error) {
      console.error('Error creating transaction:', error);
      setError(error.response?.data?.detail || 'Failed to create transaction');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 flex items-center justify-center p-4 z-[9999]"
      onMouseDown={handleBackdropMouseDown}
      onMouseUp={handleBackdropMouseUp}
    >
      <div className="fixed inset-x-0 top-16 bottom-0 bg-black bg-opacity-60 -z-10"></div>
      <div
        className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-screen overflow-y-auto"
        onMouseDown={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div className="flex items-center">
            <DollarSign className="h-6 w-6 text-blue-600 mr-2" />
            <h2 className="text-lg font-semibold text-gray-900">New Operating Transaction</h2>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {error && (
            <div className="flex items-center p-3 bg-red-50 border border-red-200 rounded-lg">
              <AlertCircle className="h-5 w-5 text-red-500 mr-2" />
              <span className="text-sm text-red-700">{error}</span>
            </div>
          )}

          {/* Credit/Debit Toggle */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Transaction Type *
            </label>
            <div className="flex space-x-4">
              <button
                type="button"
                onClick={() => setFormData(prev => ({ ...prev, is_credit: true }))}
                className={`flex-1 py-2 px-4 rounded-lg border-2 text-sm font-medium transition-colors ${
                  formData.is_credit
                    ? 'border-green-500 bg-green-50 text-green-700'
                    : 'border-gray-300 bg-white text-gray-600 hover:bg-gray-50'
                }`}
              >
                Credit (Deposit)
              </button>
              <button
                type="button"
                onClick={() => setFormData(prev => ({ ...prev, is_credit: false }))}
                className={`flex-1 py-2 px-4 rounded-lg border-2 text-sm font-medium transition-colors ${
                  !formData.is_credit
                    ? 'border-red-500 bg-red-50 text-red-700'
                    : 'border-gray-300 bg-white text-gray-600 hover:bg-gray-50'
                }`}
              >
                Debit (Withdrawal)
              </button>
            </div>
          </div>

          {/* Amount */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Amount *
            </label>
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <span className="text-gray-500 sm:text-sm">$</span>
              </div>
              <input
                type="number"
                name="amount"
                value={formData.amount}
                onChange={handleInputChange}
                step="0.01"
                min="0"
                required
                placeholder="0.00"
                className="w-full pl-7 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
          </div>

          {/* Transaction Date */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Date *
            </label>
            <input
              type="date"
              name="transaction_date"
              value={formData.transaction_date}
              onChange={handleInputChange}
              required
              className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>

          {/* Description */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Description
            </label>
            <input
              type="text"
              name="description"
              value={formData.description}
              onChange={handleInputChange}
              maxLength={500}
              placeholder="Brief description of the transaction"
              className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>

          {/* Client Name (free text) */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Client Name
            </label>
            <input
              type="text"
              name="client_name"
              value={formData.client_name}
              onChange={handleInputChange}
              maxLength={255}
              placeholder="Client associated with this transaction"
              className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>

          {/* Case */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Case (Optional)
            </label>
            <AsyncSearchableSelect
              value={formData.case_id}
              onChange={(val) => setFormData(prev => ({ ...prev, case_id: val }))}
              onSearch={searchCasesAsync}
              emptyOption="Select a case (optional)"
            />
          </div>

          {/* Payment Method */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Payment Method
            </label>
            <select
              name="payment_method"
              value={formData.payment_method}
              onChange={handleInputChange}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="">Select method</option>
              <option value="check">Check</option>
              <option value="wire">Wire Transfer</option>
              <option value="ach">ACH</option>
              <option value="cash">Cash</option>
              <option value="credit_card">Credit Card</option>
              <option value="other">Other</option>
            </select>
          </div>

          {/* Payment Notes */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Payment Notes
            </label>
            <textarea
              name="payment_notes"
              value={formData.payment_notes}
              onChange={handleInputChange}
              rows={2}
              placeholder="Check number, reference, or other notes..."
              className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>

          {/* Is Refund */}
          <div className="flex items-center">
            <input
              type="checkbox"
              id="is_refund"
              name="is_refund"
              checked={formData.is_refund}
              onChange={handleInputChange}
              className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
            />
            <label htmlFor="is_refund" className="ml-2 block text-sm text-gray-900">
              This is a refund
            </label>
          </div>

          {/* Transaction Preview */}
          <div className="bg-gray-50 rounded-lg p-4">
            <h4 className="text-sm font-medium text-gray-900 mb-2">Transaction Preview</h4>
            <div className="text-sm text-gray-600 space-y-1">
              <div>Type: {formData.is_credit ? 'Credit (Deposit)' : 'Debit (Withdrawal)'}</div>
              {formData.amount && (
                <div className={`font-medium ${formData.is_credit ? 'text-green-600' : 'text-red-600'}`}>
                  Amount: {formData.is_credit ? '+' : '-'}
                  {operatingService.formatCurrency(Math.abs(parseFloat(formData.amount) || 0))}
                </div>
              )}
              {formData.description && <div>Description: {formData.description}</div>}
              {formData.client_name && <div>Client: {formData.client_name}</div>}
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex justify-end space-x-3 pt-6 border-t border-gray-200">
            <button
              type="button"
              onClick={onClose}
              disabled={loading}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading || !formData.amount || !formData.transaction_date}
              className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-lg hover:bg-blue-700 focus:ring-2 focus:ring-blue-500 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
            >
              {loading ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  Creating...
                </>
              ) : (
                <>
                  <Check className="h-4 w-4 mr-2" />
                  Create Transaction
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default NewOperatingTransactionModal;
